import { getLocalVideoTrack } from '../base/tracks/functions';
import { setBlurContentWarning } from '../large-video';
import {
  createVirtualBackgroundEffect,
  enableBlurEffect,
  enableSensoringEffect
} from '../stream-effects/virtual-background';

import {
  BACKGROUND_ENABLED,
  BLUR_ENABLED,
  SENSORING_ENABLED,
  SET_VIRTUAL_BACKGROUND
} from './actionTypes';
import logger from './logger';

/**
 * Signals the local participant activate the virtual background video or not.
 *
 * @param {Object} options - Represents the virtual background set options.
 * @param {Object} jitsiTrack - Represents the jitsi track that will have backgraund effect applied.
 * @returns {Promise}
 */
export function toggleBackgroundEffect(options, jitsiTrack) {
  return async function (dispatch, getState) {
    await dispatch(backgroundEnabled(options.backgroundEffectEnabled));
    await dispatch(setVirtualBackground(options));
    const state = getState();
    const virtualBackground = state['feature/virtual-background'];

    if (jitsiTrack) {
      try {
        if (options.backgroundEffectEnabled) {
          await jitsiTrack.setEffect(
            await createVirtualBackgroundEffect(virtualBackground, dispatch)
          );
        } else {
          await jitsiTrack.setEffect(undefined);
          dispatch(backgroundEnabled(false));
        }
      } catch (error) {
        dispatch(backgroundEnabled(false));
        logger.error('Error on apply background effect:', error);
      }
    }
  };
}

export function toggleSensoringEffect(enabled) {
  return async function (dispatch, getState) {
    await dispatch(sensoringEnabled(enabled));
    const state = getState();
    const jitsiTrack = getLocalVideoTrack(
      state['features/base/tracks']
    )?.jitsiTrack;
    if (jitsiTrack) {
      try {
        if (enabled) {
          await jitsiTrack.setEffect(await enableSensoringEffect(state));
        } else {
          await jitsiTrack.setEffect(undefined);
          dispatch(setBlurContentWarning(undefined));
        }
      } catch (error) {
        logger.error('Error on apply sensoring effect:', error);
      }
    }
  };
}

export function toggleBlurEffect(enabled) {
  return async function (dispatch, getState) {
    await dispatch(blurEffectEnabled(enabled));
    const state = getState();
    const jitsiTrack = getLocalVideoTrack(
      state['features/base/tracks']
    )?.jitsiTrack;
    if (jitsiTrack) {
      try {
        if (enabled) {
          await jitsiTrack.setEffect(await enableBlurEffect(state));
        } else {
          await jitsiTrack.setEffect(undefined);
          dispatch(setBlurContentWarning(undefined));
        }
      } catch (error) {
        logger.error('Error on apply blur effect:', error);
      }
    }
  };
}

/**
 * Sets the selected virtual background image object.
 *
 * @param {Object} options - Represents the virtual background set options.
 * @returns {{
 *     type: SET_VIRTUAL_BACKGROUND,
 *     virtualSource: string,
 *     blurValue: number,
 *     type: string,
 * }}
 */
export function setVirtualBackground(options) {
  return {
    type: SET_VIRTUAL_BACKGROUND,
    virtualSource: options?.virtualSource,
    blurValue: options?.blurValue,
    backgroundType: options?.backgroundType,
    selectedThumbnail: options?.selectedThumbnail
  };
}

/**
 * Signals the local participant that the background effect has been enabled.
 *
 * @param {boolean} backgroundEffectEnabled - Indicate if virtual background effect is activated.
 * @returns {{
 *      type: BACKGROUND_ENABLED,
 *      backgroundEffectEnabled: boolean
 * }}
 */
export function backgroundEnabled(backgroundEffectEnabled) {
  return {
    type: BACKGROUND_ENABLED,
    backgroundEffectEnabled
  };
}

/**
 * Signals the local participant that the background effect has been enabled.
 *
 * @param {boolean} backgroundEffectEnabled - Indicate if virtual background effect is activated.
 * @returns {{
 *      type: BACKGROUND_ENABLED,
 *      backgroundEffectEnabled: boolean
 * }}
 */
export function sensoringEnabled(sensoringEffectEnabled) {
  return {
    type: SENSORING_ENABLED,
    sensoringEffectEnabled
  };
}

/**
 * Signals the local participant that the background effect has been enabled.
 *
 * @param {boolean} blurEffectEnabled - Indicate if virtual background effect is activated.
 * @returns {{
 *      type: BACKGROUND_ENABLED,
 *      blurEffectEnabled: boolean
 * }}
 */
export function blurEffectEnabled(blurEffectEnabled) {
  return {
    type: BLUR_ENABLED,
    blurEffectEnabled
  };
}
